import React, { useCallback, useEffect } from 'react';
import PropTypes from 'prop-types';
import * as Sentry from '@sentry/react';
import { BrowserTracing } from '@sentry/tracing';
import packageJson from '../../../../package.json';
import { config } from '../../../config';
import { ERROR_401_IGNORE, ERROR_PATH_FIX_IGNORE } from './constants';
import FallBack from './fallback';

const WithSentry = (props) => {
  const { children, history } = props;

  const sentryIgnite = useCallback(() => {
    try {
      Sentry.init({
        dsn: `${config.REACT_APP_SENTRY_URL}`,
        environment: config.REACT_APP_SENTRY_ENVIRONMENT || 'unknown',
        release: `${packageJson.version}`,
        integrations: [
          // The current version of react that we use on the web is not compatible with the sentry version,
          // some errors occur when using the new features and this ends up causing performance to not work.
          // TODO: Change it to use BrowserTrancing once we change React version to 18
          new BrowserTracing({
            routingInstrumentation: Sentry.reactRouterV5Instrumentation(history),
            startTransactionOnLocationChange: true,
            startTransactionOnPageLoad: true,
          }),
        ],
        // Set tracesSampleRate to 1.0 to capture 100%
        // of transactions for performance monitoring.
        // We recommend adjusting this value in production
        tracesSampler: (samplingContext) => {
          const domain = samplingContext.location?.origin;

          switch (domain) {
            case config.REACT_APP_COMPANY_PLATFORM_URI:
              return config.REACT_APP_SENTRY_SAMPLE_RATE_COMPANY;
            case config.REACT_APP_TESTER_PLATFORM_URI:
              return config.REACT_APP_SENTRY_SAMPLE_RATE_TESTER;
            case config.REACT_APP_ADMIN_PLATFORM_URI:
              return config.REACT_APP_SENTRY_SAMPLE_RATE_ADMIN;
            default:
              return config.REACT_APP_SENTRY_SAMPLE_RATE_DEFAULT;
          }
        },
        ignoreErrors: [
          ERROR_PATH_FIX_IGNORE,
          ERROR_401_IGNORE,
        ],
      });

      if (window.SessionStack) {
        window.SessionStack.getSessionId((sessionId) => {
          Sentry.withScope((scope) => {
            scope.setTag('web', 'playbook-web');
            scope.addEventProcessor((event) => {
              const newEvent = {
                ...event,
                contexts: {
                  sessionstack: {
                    session_id: sessionId,
                    timestamp: (new Date()).getTime(),
                  },
                },
              };
              return newEvent;
            });
          });
        });
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.log(error);
    }
  }, [history]);

  useEffect(() => {
    if (config.REACT_APP_ENABLE_SENTRY === 'true') {
      sentryIgnite();
    }
  }, [sentryIgnite]);

  return (
    <Sentry.ErrorBoundary
      fallback={({ error, componentStack, resetError }) => (
        <FallBack
          error={error}
          componentStack={componentStack}
          resetError={resetError}
        />
      )}
    >
      {children}
    </Sentry.ErrorBoundary>
  );
};

WithSentry.propTypes = {
  history: PropTypes.shape({}).isRequired,
  children: PropTypes.oneOfType([
    PropTypes.element.isRequired,
    PropTypes.array.isRequired,
  ]).isRequired,
};

export default WithSentry;
